if (!customElements.get('arindeka-motion-media')) {
  customElements.define('arindeka-motion-media', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.events = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = false;
      this.nearView = false;
      this.video.controls = false;
      this.video.muted = true;
      const updatePlayback = () => {
        const motionAllowed = !this.motion.matches && !document.hidden;
        if (this.nearView && motionAllowed && this.preloadObserver) {
          this.video.preload = 'auto';
          this.preloadObserver.disconnect();
          this.preloadObserver = null;
        }
        if (this.inView && motionAllowed) {
          this.video.play().catch(() => {});
        } else this.video.pause();
      };
      this.video.addEventListener('play', () => {
        if (this.motion.matches) this.video.pause();
      }, { signal: this.events.signal });
      this.motion.addEventListener('change', updatePlayback, { signal: this.events.signal });
      document.addEventListener('visibilitychange', updatePlayback, { signal: this.events.signal });
      const responsiveSource = this.video.querySelector('source[media]');
      if (responsiveSource) {
        this.video.addEventListener('playing', () => this.revealWhenReady(), { signal: this.events.signal });
        this.video.addEventListener('emptied', () => {
          this.cancelFrameReveal();
          this.classList.remove('has-video-frame');
        }, { signal: this.events.signal });
        this.preloadObserver = new IntersectionObserver(([entry]) => {
          this.nearView = entry.isIntersecting;
          updatePlayback();
        }, { rootMargin: '600px 0px' });
        this.preloadObserver.observe(this.video);
        this.sourceMedia = matchMedia(responsiveSource.media);
        this.resumeTime = null;
        this.video.addEventListener('loadedmetadata', () => {
          if (this.resumeTime !== null && Number.isFinite(this.video.duration)) {
            this.video.currentTime = Math.min(this.resumeTime, this.video.duration);
            this.resumeTime = null;
          }
        }, { signal: this.events.signal });
        this.sourceMedia.addEventListener('change', () => {
          this.resumeTime ??= this.video.currentTime;
          this.video.load();
          updatePlayback();
        }, { signal: this.events.signal });
      }
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        updatePlayback();
      }, { threshold: 0.25 });
      this.observer.observe(this.video);
    }
    revealWhenReady() {
      if (this.classList.contains('has-video-frame') || this.frameCallback != null || this.paintCallback != null) return;
      const reveal = () => {
        this.frameCallback = null;
        this.paintCallback = null;
        if (!this.video.paused && this.video.readyState >= 2 && !this.motion.matches && !document.hidden) {
          this.classList.add('has-video-frame');
        }
      };
      if (this.video.requestVideoFrameCallback) {
        this.frameCallback = this.video.requestVideoFrameCallback(reveal);
      } else {
        // Older browsers need a paint boundary after playback has started.
        this.paintCallback = requestAnimationFrame(() => {
          this.paintCallback = requestAnimationFrame(reveal);
        });
      }
    }
    cancelFrameReveal() {
      if (this.frameCallback != null) this.video.cancelVideoFrameCallback(this.frameCallback);
      if (this.paintCallback != null) cancelAnimationFrame(this.paintCallback);
      this.frameCallback = null;
      this.paintCallback = null;
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.preloadObserver?.disconnect();
      this.cancelFrameReveal();
      this.video?.pause();
      this.events = null;
      this.observer = null;
      this.preloadObserver = null;
      this.sourceMedia = null;
      this.resumeTime = null;
    }
  });
}
